/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';

import {
	convertProduction,
	parseFcApplicable,
} from '@utils/functions';
import { formatDate } from '@utils/functions';
import { NOT_APPLICABLE } from '@config/constants/common';
import isNil from 'lodash/isNil';
import useStore from '@store/index';
import {
	getConvertionFactor,
	parseTokenEquivalentToTCO2E,
} from '@utils/productions';

export interface ApiResponse {
	rows: any[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export const getProductions = (
	params: any
): Promise<ApiResponse> => {
	return apiClient.get('/productions', {
		params: {
			populate: [
				'project',
				'site',
				'conversionFactor',
				'organization',
				'entity',
				'aco2Token',
				'meterPoint',
			],
			query: {
				fromImport: params.importHistoryId,
			},
			sort: `${params.sortParams ?? ''} _id`,
			pageSize: params.importLinesCount,
		},
	});
};

export const useProductions = (
	queryParams: any,
	sortParams: any,
	importHistoryId: string,
	importLinesCount: number
) => {
	const {
		setImportHistoriesHasVerifiedOrMintedProduction,
	} = useStore();

	const { data, isFetching, isFetched } = useQuery({
		staleTime: 500,
		queryKey: [
			'productions-rollback',
			{
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getProductions({
				queryParams: queryParams,
				sortParams: sortParams,
				importHistoryId: importHistoryId,
				importLinesCount: importLinesCount,
			}),
		enabled: !!importHistoryId,
	});

	let importHasVerifiedOrMintedProduction = null;
	if (
		isFetched &&
		!isNil(data) &&
		!isNil(data.rows) &&
		Array.isArray(data.rows)
	) {
		try {
			const filteredImportHistories = data.rows.filter(
				(row) => row.status == 'MINTED' || row.verified
			);

			importHasVerifiedOrMintedProduction =
				filteredImportHistories.length > 0;
		} catch (error) {
			console.log('error : ', error);
		}
	}

	const updateList = (productions: any) =>
		productions.reduce((acc: any, currentItem: any) => {
			// Fc applicable
			const fcApplicable = parseFcApplicable(
				currentItem.conversionFactorOverride,
				currentItem.conversionFactor,
				currentItem.unit
			);

			const convertionFactor = getConvertionFactor(
				currentItem.conversionFactorOverride,
				currentItem.conversionFactor
			);

			// Mint status
			const { status } = currentItem;
			const blockchain =
				currentItem &&
				currentItem.aco2Token &&
				currentItem.aco2Token.blockchain
					? currentItem.aco2Token.blockchain
					: NOT_APPLICABLE;

			const transactionHash =
				currentItem &&
				currentItem.aco2Token &&
				currentItem.aco2Token.transactionHash
					? currentItem.aco2Token.transactionHash
					: NOT_APPLICABLE;

			const mintStatus = status ?? 'None';
			const modifiedItem = {
				...currentItem,
				blockchain: blockchain,
				transactionHash: transactionHash,
				site: currentItem.site
					? currentItem.site.name
					: NOT_APPLICABLE,
				meterPoint: currentItem.meterPoint
					? currentItem.meterPoint.serialNumber
					: NOT_APPLICABLE,
				projectId: currentItem.project
					? currentItem.project.id
					: NOT_APPLICABLE,
				project: currentItem.project
					? currentItem.project.name
					: NOT_APPLICABLE,
				startDate: formatDate(
					currentItem.startDate,
					'DD/MM/YYYY'
				),
				endDate: formatDate(
					currentItem.endDate,
					'DD/MM/YYYY'
				),
				production: convertProduction(
					currentItem.quantity,
					currentItem.unit
				),
				tokenEquivalent: parseTokenEquivalentToTCO2E(
					convertionFactor,
					currentItem.quantity
				),
				fcApplicable: fcApplicable,
				mintStatus: mintStatus,
			};

			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
		importHasVerifiedOrMintedProduction,
	};
};
